<template>
  <!-- A primeira carga (e o retry depois de um erro nela) fica sob o esqueleto.
       A barra de filtros só monta DEPOIS: quando ela nasce, os filtros da URL
       já estão na store (hidratados no setup, antes do primeiro render). -->
  <div v-if="isFirstLoadPending" aria-busy="true" data-testid="priority-payments-loading">
    <span class="sr-only">Carregando pagamentos prioritários</span>
    <PriorityPaymentsSkeleton />
  </div>

  <div
    v-else
    class="flex w-full flex-col gap-4 text-left"
    data-testid="priority-payments"
    :data-refresh-paused="String(isRefreshPaused)"
  >
    <!-- Ordem do M3 (spec §8.1): abas → relógio → filtros → atalhos → visões. -->
    <PriorityPaymentsTabs
      :tab="store.filters.tab"
      :counts="store.response?.slaCounters?.byTab ?? null"
      @update:tab="onSelectTab"
    />

    <PrioritySlaClock
      v-if="store.response"
      :bands="store.response.bands ?? null"
      :tab="store.filters.tab"
      :active-band="store.filters.slaBand"
      :now-ms="nowMs"
      :config="store.response.config"
      test-id="priority-payments-clock"
      @toggle-band="onToggleBand"
    />

    <PriorityPaymentsFilterBar
      :filters="store.filters"
      :cycle-label="store.response?.cycle?.label ?? null"
      :listed-until="store.response?.cycle?.listedUntil ?? null"
      :last-loaded-label="lastLoadedLabel"
      :is-refreshing="store.isRefetching"
      :is-exporting="store.isExporting"
      :inferred-operational-group="inferredOperationalGroup"
      :doctor-options="store.response?.facets?.doctors ?? NO_DOCTOR_FACETS"
      :situation-counts="store.response?.counters.bySituation ?? null"
      :antecipe-paid-unconfirmed-count="store.response?.antecipePaidUnconfirmed30d?.count ?? null"
      @update:filters="onFiltersPatch"
      @refresh="onRefresh"
      @export="onExport"
      @export-antecipe-paid-unconfirmed="onExportAntecipePaidUnconfirmed"
    />

    <div v-if="store.response" class="flex flex-wrap items-center gap-2">
      <PriorityPaymentsCounters
        :by-state="store.response.slaCounters?.byState ?? null"
        :overdue-carry-over="store.response.slaCounters?.overdueCarryOver ?? null"
        :active-states="store.filters.slaStates"
        :is-overdue-only="store.filters.onlyOverdue"
        @select-states="onSelectStates"
        @select-overdue="onSelectOverdue"
      />
      <!-- Selo "Só: …" da linha do relógio ligada (M3). Fica aqui, e não no
           relógio, porque a linha some com pending = 0 e o selo precisa
           continuar para desligar o filtro. -->
      <span
        v-if="store.filters.slaBand"
        class="inline-flex items-center gap-1 rounded-full bg-primary5 px-2.5 py-1 text-xs font-medium text-primary40"
        data-testid="priority-payments-band-filter"
      >
        Só: {{ formatSlaBandLabel(store.filters.slaBand, store.response.config) }}
        <button
          type="button"
          class="px-1 text-sm leading-none"
          aria-label="Remover filtro do prazo"
          data-testid="priority-payments-band-filter-clear"
          @click="onToggleBand(store.filters.slaBand)"
        >
          ×
        </button>
      </span>
    </div>

    <!-- Duas informações que ficam FORA dos totais de propósito (Q3 e Q12):
         exceção ainda em aprovação não é plantão À vista confirmado, e o
         Antecipe pago dos últimos 30 dias não depende do período da tela. -->
    <div
      v-if="store.response"
      class="flex flex-wrap gap-x-[18px] gap-y-2 rounded-lg border border-dashed border-lightGray30 px-3 py-2 text-[13px] text-neutral50"
      data-testid="priority-payments-strip"
    >
      <span>
        À vista <b class="text-textPrimary">em aprovação</b> na janela:
        <b class="tabular-nums text-textPrimary">{{ store.response.pendingApproval.count }}</b>
        <span class="text-xs"> (fora dos totais)</span>
      </span>
      <span>
        Antecipe <b class="text-textPrimary">pago</b> sem horário confirmado, últimos 30 dias:
        <b class="tabular-nums text-red-700">{{
          store.response.antecipePaidUnconfirmed30d.count
        }}</b>
      </span>
    </div>

    <QueueAttackPanel
      v-if="attack.isActive.value"
      :is-active="attack.isActive.value"
      :is-finished="attack.isFinished.value"
      :position="attack.position.value"
      :total="attack.total.value"
      :title="attack.current.value?.doctorName ?? ''"
      :subtitle="String(attack.current.value?.doctorStationId ?? '')"
      :next-label="nextAttackLabel"
      :done-count="attack.doneCount.value"
      :skipped-count="attack.skippedCount.value + attackMissingCount - attackTakenCount"
      :taken-count="attackTakenCount"
      :busy="attack.isBusy.value"
      test-id="priority-payments-attack-panel"
      @done="onAttackDone"
      @skip="onAttackSkip"
      @quit="onFinishAttack"
      @finish="onFinishAttack"
    >
      <template #clock>
        <!-- O relógio do ataque é o countdown do item atual (spec §8.2). -->
        <PrioritySlaCountdown
          v-if="attack.current.value"
          :sla="attack.current.value.sla ?? null"
          :row="attack.current.value"
          :now-ms="nowMs"
          :thresholds="attackThresholds"
          size="lg"
          test-id="priority-payments-attack-clock"
        />
      </template>
      <template #facts>
        <!-- Ruling C9: o cartão do ataque também tem destino para as ações. -->
        <PriorityPaymentCard
          v-if="attack.current.value"
          :row="attack.current.value"
          :now-ms="nowMs"
          :config="config"
          @action="onRowAction"
        />
      </template>
      <template #message>
        <p
          v-if="attackMessage"
          class="m-0 max-w-[70ch] rounded-md bg-lightGray30 p-2.5 text-[13px] text-neutral50"
          data-testid="priority-payments-attack-message"
        >
          {{ attackMessage }}
        </p>
        <p
          v-if="attackMissingMessage"
          class="m-0 text-xs text-red-700"
          role="status"
          data-testid="priority-payments-attack-missing"
        >
          {{ attackMissingMessage }}
        </p>
        <p
          v-if="attackConflictMessage"
          class="m-0 text-xs text-red-700"
          role="status"
          data-testid="priority-payments-attack-conflict"
        >
          {{ attackConflictMessage }}
        </p>
      </template>
      <template #summary>
        <p
          v-if="attackConflictMessage"
          class="m-0 text-xs text-red-700"
          role="status"
          data-testid="priority-payments-attack-conflict"
        >
          {{ attackConflictMessage }}
        </p>
      </template>
      <template #actions>
        <button
          v-if="attackPrimaryLabel"
          type="button"
          class="rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white"
          data-testid="priority-payments-attack-primary"
          @click="onAttackPrimary"
        >
          {{ attackPrimaryLabel }}
        </button>
        <button
          v-if="attackMessage"
          type="button"
          class="rounded-md border border-lightGray30 px-3 py-1.5 text-sm font-medium text-textPrimary"
          data-testid="priority-payments-attack-copy"
          @click="onCopyAttackMessage"
        >
          {{ copyFeedback ?? 'Copiar mensagem' }}
        </button>
        <button
          v-if="attack.current.value"
          type="button"
          class="rounded-md border border-lightGray30 px-3 py-1.5 text-sm font-medium text-textPrimary"
          data-testid="priority-payments-attack-details"
          @click="goToDetails(attack.current.value)"
        >
          Detalhes do plantão
        </button>
      </template>
    </QueueAttackPanel>

    <!-- `:key` por plantão: trocar de plantão remonta o formulário, sem
         carregar canal e observação digitados para o plantão errado. -->
    <PriorityContactForm
      v-if="contactRow"
      :key="contactRow.doctorStationId"
      :doctor-station-id="contactRow.doctorStationId"
      :doctor-name="contactRow.doctorName"
      :default-channel="contactDefaultChannel"
      :is-saving="isSavingContact"
      @submit="onSubmitContact"
      @cancel="contactRow = null"
    />

    <div class="flex flex-wrap items-end gap-3">
      <QueueViewSwitcher
        v-model="viewMode"
        :screen-key="PRIORITY_SCREEN_KEY"
        test-id="priority-payments-view-switcher"
      />
      <!-- Um valor só para Lista e Kanban; a Tabela é plana, ordenada pelo prazo. -->
      <PriorityGroupBySelect
        :model-value="groupBy"
        :disabled="viewMode === 'table'"
        @update:model-value="setGroupBy"
      />
    </div>

    <!-- Erro depois da primeira resposta: as linhas do recorte anterior FICAM
         (a store não as apaga), então a tela avisa que podem não corresponder
         aos filtros e à página de agora. -->
    <div
      v-if="store.loadError && store.hasLoaded"
      class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
      role="alert"
      data-testid="priority-payments-stale-warning"
    >
      <span class="flex-1">
        Não foi possível atualizar a lista. Os plantões exibidos podem estar desatualizados.
        <span
          v-if="store.loadError !== PRIORITY_LIST_ERROR"
          class="block text-xs"
          data-testid="priority-payments-stale-warning-message"
        >
          {{ store.loadError }}
        </span>
      </span>
      <button
        type="button"
        class="rounded-md border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-700"
        :disabled="store.isLoading || store.isRefetching"
        data-testid="priority-payments-stale-warning-retry"
        @click="onRetry"
      >
        Tentar novamente
      </button>
    </div>

    <PriorityPaymentsErrorState
      v-if="store.loadError && !store.hasLoaded"
      :message="store.loadError"
      @retry="onRetry"
    />

    <PriorityPaymentsEmptyState
      v-else-if="store.hasLoaded && !store.rows.length"
      :has-filters="hasFilters"
      @clear-filters="onClearFilters"
    />

    <template v-else>
      <QueueGroupedList
        v-if="viewMode === 'list'"
        :groups="listSections"
        :count-label="countLabel"
        test-id="priority-payments-view-list"
      >
        <template #title="{ group }">
          <PrioritySlaStateBadge
            v-if="groupStates.get(group.key)"
            :state="groupStates.get(group.key)!"
            :test-id="`priority-payments-group-${group.key}`"
          />
          <span
            v-else
            class="rounded-full px-2 py-0.5 text-xs font-medium"
            :class="QUEUE_TONE_PILL_CLASS[group.tone ?? 'neutral']"
          >
            {{ group.title }}
          </span>
        </template>
        <template #default="{ group }">
          <PriorityPaymentsTable
            :rows="group.rows"
            :server-now="serverNow"
            :now-ms="nowMs"
            :config="config"
            :test-id="`priority-payments-list-table-${group.key}`"
            @action="onRowAction"
          />
        </template>
      </QueueGroupedList>

      <PriorityPaymentsTable
        v-else-if="viewMode === 'table'"
        :rows="store.rows"
        :server-now="serverNow"
        :now-ms="nowMs"
        :config="config"
        :sort="sort"
        :selectable="true"
        :selected-ids="store.selectedIds"
        :is-row-selectable="isSelectableRow"
        test-id="priority-payments-view-table"
        @update:selected-ids="store.setSelectedIds"
        @update:sort="onSortChange"
        @action="onRowAction"
      />

      <!-- O `data-testid` da tela fica no invólucro: o do Kanban continua o
           padrão (`queue-kanban-column-<key>`). -->
      <div v-else data-testid="priority-payments-view-kanban">
        <!-- Carga por coluna: total real no cabeçalho e "Ver mais" por grupo. -->
        <QueueKanban
          :rows="kanban.rows.value"
          :columns="kanbanColumns"
          :row-key="rowKey"
          :column-totals="kanban.totals.value"
          :column-has-more="kanban.hasMore.value"
          :column-loading="kanban.loading.value"
          @load-more="kanban.loadMore"
        >
          <template #column-title="{ column }">
            <PrioritySlaStateBadge
              v-if="groupStates.get(column.key)"
              :state="groupStates.get(column.key)!"
            />
            <template v-else>{{ column.title }}</template>
          </template>
          <template #card="{ row }">
            <!-- Ruling C9: o cartão liga `@action` ao mesmo handler da página. -->
            <PriorityPaymentCard
              :row="row"
              :now-ms="nowMs"
              :config="config"
              :selectable="isSelectableRow(row)"
              :is-selected="store.selectedIds.includes(row.doctorStationId)"
              @action="onRowAction"
              @toggle-select="onToggleSelect"
            />
          </template>
        </QueueKanban>
      </div>

      <v-pagination
        v-if="store.pageCount > 1"
        :model-value="store.page"
        :total-visible="7"
        :length="store.pageCount"
        :disabled="store.isRefetching || attack.isActive.value"
        density="comfortable"
        color="primary"
        data-testid="priority-payments-pagination"
        @update:model-value="onPageChange"
      />
    </template>

    <QueueBulkBar
      v-if="!attack.isActive.value && (attackPendingCount > 0 || store.selectedIds.length > 0)"
      :selected-count="store.selectedIds.length"
      :pending-count="attackPendingCount"
      pending-label="com prazo pendente"
      :primary-label="bulkPrimaryLabel"
      :actions="BULK_ACTIONS"
      test-id="priority-payments-bulk-bar"
      @primary="onStartAttack"
      @action="onBulkAction"
      @clear="store.clearSelection"
    />
  </div>

  <SendRecordModal
    v-if="sendRecordContext"
    :model-value="isSendRecordOpen"
    :context="sendRecordContext"
    :doctor-station-id="sendRecordContext.doctorStationId"
    :schedule-id="sendRecordContext.scheduleId"
    @update:model-value="onSendRecordOpenChange"
    @saved="onRecordSaved"
    @redirect-to-sheet="onRedirectToSheet"
  />

  <!--
    #3097 — "Registro de Plantão" no passo 1 do SendRecordModal reabre o wizard de folha
    AQUI, com os campos da vaga preenchidos e travados (C6), como nas demais telas.
  -->
  <AttendanceUploadModal
    v-if="isSheetUploadOpen"
    v-model="isSheetUploadOpen"
    :initial-form="sheetInitialForm"
    :locked-fields="sheetLockedFields"
    @save="onSheetSaved"
  />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import QueueAttackPanel from '@/components/designSystem/QueueViews/QueueAttackPanel.vue'
import QueueBulkBar from '@/components/designSystem/QueueViews/QueueBulkBar.vue'
import QueueGroupedList from '@/components/designSystem/QueueViews/QueueGroupedList.vue'
import QueueKanban from '@/components/designSystem/QueueViews/QueueKanban.vue'
import QueueViewSwitcher from '@/components/designSystem/QueueViews/QueueViewSwitcher.vue'
import { QUEUE_TONE_PILL_CLASS } from '@/components/designSystem/QueueViews/types'
import type {
  QueueBulkAction,
  QueueGroup,
  QueueSort,
  QueueViewMode
} from '@/components/designSystem/QueueViews/types'
import { useAttackQueue } from '@/components/designSystem/QueueViews/useAttackQueue'
import type { DsAttendanceUploadForm } from '@/components/designSystem/modals/AttendanceUploadModal/types'
import SendRecordModal from '@/components/designSystem/modals/SendRecordModal/Index.vue'
import { buildSheetHandoff } from '@/components/designSystem/modals/SendRecordModal/types'
import type {
  RedirectToSheetPayload,
  SendRecordContext
} from '@/components/designSystem/modals/SendRecordModal/types'
import PriorityPaymentsSkeleton from '@/components/designSystem/Skeleton/PriorityPaymentsSkeleton.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import {
  PRIORITY_LIST_ERROR,
  usePriorityPaymentsStore
} from '@/pinia-store/usePriorityPaymentsStore'
import { DEFAULT_UTC_OFFSET, formatHourMinuteInOffset } from '@/utils/date'
import PriorityPaymentsService from '@/Network/Services/PriorityPaymentsService'
import type {
  PriorityContactChannel,
  PriorityDoctorFacetDto,
  PriorityPaymentRow,
  PrioritySlaBand,
  PrioritySlaState,
  PriorityTab
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import PriorityContactForm from './components/PriorityContactForm.vue'
import PriorityPaymentCard from './components/PriorityPaymentCard.vue'
import PriorityPaymentsCounters from './components/PriorityPaymentsCounters.vue'
import PriorityPaymentsEmptyState from './components/PriorityPaymentsEmptyState.vue'
import PriorityPaymentsErrorState from './components/PriorityPaymentsErrorState.vue'
import PriorityPaymentsFilterBar from './components/PriorityPaymentsFilterBar.vue'
import PriorityPaymentsTabs from './components/PriorityPaymentsTabs.vue'
import PrioritySlaClock from './components/PrioritySlaClock.vue'
import PriorityGroupBySelect from './components/PriorityGroupBySelect.vue'
import PriorityPaymentsTable from './components/PriorityPaymentsTable.vue'
import PrioritySlaCountdown from './components/PrioritySlaCountdown.vue'
import PrioritySlaStateBadge from './components/PrioritySlaStateBadge.vue'
import { usePriorityGrouping } from './composables/usePriorityGrouping'
import { usePrioritySlaTick } from './composables/usePrioritySlaTick'
import {
  PRIORITY_SLA_PENDING_STATES,
  formatSlaBandLabel,
  formatSlaKindLabel,
  priorityThresholdsFor
} from './slaFormat'
import { priorityAuditRoute, priorityDetailsRoute, priorityQueueReturnTo } from './actions'
import type { PriorityActionDescriptor } from './actions'
import {
  PRIORITY_SCREEN_KEY,
  priorityColumnDefinitions,
  priorityGroupColumns,
  priorityGroupSections
} from './constants'
import { hasActivePriorityFilters, priorityFiltersFromUrl, priorityFiltersToUrl } from './filters'
import type { PriorityPaymentsFilters } from './filters'
import {
  PRIORITY_COPY_FEEDBACK,
  buildPriorityBulkCollectionMessage,
  buildPriorityCollectionMessage
} from './collectionMessages'
import { PRIORITY_SLA_SORT_KEY_TO_ORDER_BY } from './tableColumns'
import { usePriorityPaymentsAutoRefresh } from './composables/usePriorityPaymentsAutoRefresh'
import { usePriorityKanbanColumns } from './composables/usePriorityKanbanColumns'
import { buildPrioritySendRecordContext } from './sendRecordContext'

/** Wizard de folha: pesado e raro aqui — só baixa quando "Registro de Plantão" o abre. */
const AttendanceUploadModal = defineAsyncComponent(
  () => import('@/components/designSystem/modals/AttendanceUploadModal/Index.vue')
)

type RowActionPayload = { key: PriorityActionDescriptor['key']; row: PriorityPaymentRow }

/** Fallback estável: um `[]` literal no template seria um array novo a cada tique. */
const NO_DOCTOR_FACETS = Object.freeze<PriorityDoctorFacetDto>([])

const route = useRoute()
const router = useRouter()
const store = usePriorityPaymentsStore()
const dsAttendanceStore = useDsAttendanceStore()
const { notifyError, notifyErrorMessage } = useErrorToast()

// A URL manda no recorte. Hidratar AQUI, e não no `onMounted`, garante que a
// barra de filtros nunca veja os filtros padrão antes dos do link.
store.hydrate(priorityFiltersFromUrl(route.query as Record<string, unknown>))

// Padrão da tela é o Kanban (pedido do dono); a preferência gravada pelo
// QueueViewSwitcher vence no mount.
const viewMode = ref<QueueViewMode>('kanban')

/**
 * Um tique só para a tela inteira, ancorado no `serverNow` da última resposta
 * (spec §8.2). Chips, countdowns, relógio e cartões recebem `nowMs` por prop.
 */
const { nowMs } = usePrioritySlaTick(() => store.response?.serverNow ?? null)

/**
 * Sem "Exportar seleção": o `/export` só aceita o recorte, não ids (spec) — o
 * "Exportar" da barra de filtros já cobre o filtro inteiro.
 */
const BULK_ACTIONS: QueueBulkAction[] = [{ key: 'copy', label: 'Copiar cobrança em lote' }]

const ROWS_MISSING_MESSAGE = 'Alguns plantões não puderam ser carregados e foram pulados.'
const ATTACK_EMPTY_MESSAGE =
  'Nenhum plantão disponível para o ataque neste recorte. Os pendentes podem estar com outra pessoa.'

const isSendRecordOpen = ref(false)
const sendRecordContext = ref<SendRecordContext | null>(null)
const isSheetUploadOpen = ref(false)
const sheetInitialForm = ref<Partial<DsAttendanceUploadForm> | null>(null)
const sheetLockedFields = ref<string[]>([])
/** Plantão do wizard de folha: o contexto do SendRecordModal some quando ele fecha. */
const sheetDoctorStationId = ref<number | null>(null)
const copyFeedback = ref<string | null>(null)
const attackConflictMessage = ref<string | null>(null)
const attackMissingMessage = ref<string | null>(null)

/**
 * Retrato de TODAS as linhas da fila de ataque (da página e de fora dela),
 * tirado no início do ataque. Um plantão tratado sai do filtro ou da página —
 * o "Não enviado" que virou "Em auditoria" some do `fetchList` — e sem o
 * retrato o painel ficaria em branco e o "Tratado, próximo" não contaria.
 */
const attackRows = ref(new Map<number, PriorityPaymentRow>())
/** Ids da fila que nem a busca trouxe: entram no resumo como pulados. */
const attackMissingCount = ref(0)
/** Conflitos de marca (409): outra pessoa já tratava. Saem de "pulados" no resumo. */
const attackTakenCount = ref(0)

/** Plantão cujo contato está sendo registrado; `null` = formulário fechado. */
const contactRow = ref<PriorityPaymentRow | null>(null)
const contactDefaultChannel = ref<PriorityContactChannel>('WHATSAPP')
const isSavingContact = ref(false)

/** Ação da barra de lote em voo: a barra não desabilita os botões (Task 8). */
const isBulkActionRunning = ref(false)

/**
 * Candidatos do ataque (spec §3.9): SLA principal PENDENTE (Estourado, Urgente,
 * Atenção, No prazo), inclusive "Aguardando auditoria". O número é o do
 * SERVIDOR para o filtro inteiro (`slaCounters.byState` respeita aba e
 * `slaBand` e ignora o filtro de estado): com estados filtrados, conta só os
 * pendentes entre eles. Sem `slaCounters` (BE anterior ao SLA), volta ao
 * "precisa enviar" antigo.
 */
const attackPendingCount = computed(() => {
  const response = store.response
  if (!response) return 0
  const byState = response.slaCounters?.byState
  if (!byState) return response.counters.needsSending
  const { slaStates } = store.filters
  const states = slaStates.length
    ? PRIORITY_SLA_PENDING_STATES.filter((state) => slaStates.includes(state))
    : PRIORITY_SLA_PENDING_STATES
  return states.reduce((total, state) => total + (byState[state] ?? 0), 0)
})

const isSelectableRow = (row: PriorityPaymentRow): boolean =>
  row.situation === 'NOT_SENT' || row.situation === 'REJECTED' || row.situation === 'AWAITING_AUDIT'

const onToggleSelect = (doctorStationId: number): void => {
  store.setSelectedIds(
    store.selectedIds.includes(doctorStationId)
      ? store.selectedIds.filter((id) => id !== doctorStationId)
      : [...store.selectedIds, doctorStationId]
  )
}

const bulkPrimaryLabel = computed(() => {
  const selected = store.selectedIds.length
  if (selected === 1) return 'Atacar o 1 selecionado'
  if (selected > 1) return `Atacar os ${selected} selecionados`
  return `Atacar em sequência (${attackPendingCount.value})`
})

/**
 * A fila do ataque é o retrato tirado no início, com a linha fresca da página
 * por cima quando ela ainda está lá: o `useAttackQueue` guarda IDS e resolve a
 * linha aqui, e um plantão não some da fila por sair da página ou do filtro.
 */
const attack = useAttackQueue<PriorityPaymentRow & { id: number }>({
  items: computed(() => {
    const pageById = new Map(store.rows.map((row) => [row.doctorStationId, row]))
    return [...attackRows.value.values()].map((row) => ({
      ...(pageById.get(row.doctorStationId) ?? row),
      id: row.doctorStationId
    }))
  }),
  onEnter: async (item) => {
    // O aviso de conflito NÃO é zerado aqui (ruling C3): o `onEnter` do item
    // seguinte roda logo depois do `onEnterError` e apagaria o aviso do 409
    // antes de ele chegar à tela.
    copyFeedback.value = null
    await store.claim(item.doctorStationId)
  },
  onLeave: async (item) => {
    // Soltar a marca é best-effort: se falhar, ela expira sozinha em 30 min e
    // travar a fila da pessoa por causa disso seria pior do que o conflito.
    await store.releaseClaim(item.doctorStationId).catch(() => undefined)
  },
  onEnterError: (_item, error) => {
    const isConflict = PriorityPaymentsService.isClaimConflict(error)
    if (isConflict) attackTakenCount.value += 1
    attackConflictMessage.value = isConflict
      ? 'Outra pessoa já está tratando este plantão. Pulamos para o próximo.'
      : 'Não foi possível abrir este plantão. Pulamos para o próximo.'
  }
})

/** "A seguir: <médico> · <tipo de SLA>" (M3); sem SLA, a unidade. */
const nextAttackLabel = computed(() => {
  const next = attack.next.value
  if (!next) return null
  const detail = next.sla
    ? formatSlaKindLabel(next.sla, next, nowMs.value)
    : (next.healthUnitName ?? next.scheduleName)
  return `${next.doctorName} · ${detail}`
})

const attackMessage = computed(() => {
  const current = attack.current.value
  if (!current || current.situation === 'AWAITING_AUDIT') return null
  return buildPriorityCollectionMessage(current)
})

const attackPrimaryLabel = computed(() => {
  const current = attack.current.value
  if (!current) return null
  if (current.situation === 'AWAITING_AUDIT') {
    return current.allowedActions.includes('AUDIT') ? 'Auditar agora' : null
  }
  if (!current.allowedActions.includes('SEND_RECORD')) return null
  return current.situation === 'REJECTED' ? 'Reenviar registro agora' : 'Enviar registro agora'
})

const attackThresholds = computed(() => {
  const sla = attack.current.value?.sla
  return sla ? priorityThresholdsFor(store.response?.config, sla.kind) : null
})

// A atualização automática não pode trocar as linhas debaixo de um modal ou
// formulário aberto, nem no meio do ataque: o plantão exibido mudaria embaixo
// da pessoa — e um contato seria gravado no plantão errado.
const isRefreshPaused = computed(
  () =>
    isSendRecordOpen.value ||
    isSheetUploadOpen.value ||
    attack.isActive.value ||
    contactRow.value !== null
)
usePriorityPaymentsAutoRefresh(isRefreshPaused)

/** Esqueleto até a primeira resposta — e de novo durante o retry de um erro nela. */
const isFirstLoadPending = computed(() => !store.hasLoaded && (store.isLoading || !store.loadError))

const rowKey = (row: PriorityPaymentRow): number => row.doctorStationId

const { groupBy, setGroupBy } = usePriorityGrouping(useAuthStore().getUserId)

const config = computed(() => store.response?.config ?? null)
const serverNow = computed(() => store.response?.serverNow ?? new Date().toISOString())

/**
 * Grupos pelo "Agrupar por" escolhido, com o estado AO VIVO (o tique move a
 * linha de Atenção para Urgente sem esperar o refresh). Sem coluna "Atrasados"
 * (Q6/Q19). Dentro do grupo vale a ordem do servidor.
 */
const groupingContext = computed(() => ({ nowMs: nowMs.value, config: config.value }))
/**
 * Kanban: linhas carregadas POR COLUNA (os primeiros N de cada grupo + "Ver
 * mais"), não a página da Lista. Lista e Tabela continuam na `store.rows`.
 */
const kanban = usePriorityKanbanColumns({
  groupBy,
  isActive: computed(() => viewMode.value === 'kanban'),
  onLoadMoreError: (error) => notifyError(error, 'Não foi possível carregar mais plantões')
})
const kanbanColumns = computed(() =>
  priorityGroupColumns(groupBy.value, kanban.rows.value, groupingContext.value, kanban.totals.value)
)
const listSections = computed(() =>
  priorityGroupSections(groupBy.value, store.rows, groupingContext.value)
)
/** Colunas/seções de estado mostram o selo do estado no título. */
const groupStates = computed(
  () =>
    new Map<string, PrioritySlaState>(
      priorityColumnDefinitions(groupBy.value, groupingContext.value).flatMap((column) =>
        column.state ? [[column.key, column.state] as [string, PrioritySlaState]] : []
      )
    )
)
const countLabel = (group: QueueGroup<PriorityPaymentRow>): string =>
  `${group.rows.length} ${group.rows.length > 1 ? 'plantões' : 'plantão'}`

const hasFilters = computed(() => hasActivePriorityFilters(store.filters))

const lastLoadedLabel = computed(() =>
  store.lastLoadedAt ? formatHourMinuteInOffset(store.lastLoadedAt, DEFAULT_UTC_OFFSET) : null
)

/**
 * Direção em que o SERVIDOR entrega cada `orderBy` (o contrato não aceita
 * direção): prazo do mais urgente para o mais folgado; plantão do mais antigo
 * para o mais novo. A seta e o `aria-sort` dizem essa verdade.
 */
const PRIORITY_SORT_DIRECTION: Record<string, QueueSort['direction']> = {
  time: 'asc',
  station: 'asc'
}

/** A seta da Tabela reflete o `orderBy` do recorte (inclusive o que veio pela URL). */
const sort = computed<QueueSort | null>(() => {
  const key = Object.keys(PRIORITY_SLA_SORT_KEY_TO_ORDER_BY).find(
    (candidate) => PRIORITY_SLA_SORT_KEY_TO_ORDER_BY[candidate] === store.filters.orderBy
  )
  return key ? { key, direction: PRIORITY_SORT_DIRECTION[key] ?? 'asc' } : null
})

/**
 * Grupo operacional sugerido à barra de filtros: quando a fila inteira (cabe
 * numa página) pertence a UM grupo, a pessoa quase certamente é daquele grupo.
 *
 * Avaliado UMA vez, na primeira resposta com sucesso, qualquer que seja o
 * resultado: reavaliar no refresh de 60 s, no Atualizar ou na paginação
 * aplicaria sozinho um filtro no meio da sessão. Linha sem grupo conta como um
 * valor distinto — pré-selecionar o grupo esconderia essas linhas.
 */
const inferredOperationalGroup = ref<string | null>(null)
let hasEvaluatedInferredGroup = false

watch(
  () => store.response,
  (response) => {
    if (!response || hasEvaluatedInferredGroup) return
    hasEvaluatedInferredGroup = true
    if (store.filters.operationalGroups.length) return
    if (response.count > response.data.length) return
    const groups = new Set(response.data.map((row) => row.operationalGroup?.trim() ?? ''))
    const [only] = [...groups]
    if (groups.size === 1 && only) inferredOperationalGroup.value = only
  }
)

const goToDetails = (row: PriorityPaymentRow): void => {
  void router.push(priorityDetailsRoute(row))
}

const goToAudit = (row: PriorityPaymentRow): void => {
  const target = priorityAuditRoute(row, priorityQueueReturnTo(route.fullPath))
  if (target) void router.push(target)
}

/**
 * Abre o "Enviar registro" identificando o plantão (BUG-UI-03). Antes de abrir,
 * carrega a configuração de registro DA ESCALA do plantão: é ela que diz se o
 * passo 1 pode oferecer folha (escala só-app bloqueia). A store não relança; se
 * a consulta falhar, o modal abre sem política, como o Detalhes do Plantão.
 */
async function openSendRecord(row: PriorityPaymentRow): Promise<void> {
  await dsAttendanceStore.fetchRegistrationSettings(row.scheduleId)
  const settings =
    dsAttendanceStore.registrationSettingsScheduleId === row.scheduleId
      ? dsAttendanceStore.registrationSettings
      : null
  const companyId =
    dsAttendanceStore.filterSchedules.find((schedule) => schedule.value === row.scheduleId)
      ?.companyId ?? null
  sendRecordContext.value = buildPrioritySendRecordContext(row, settings, companyId)
  isSendRecordOpen.value = true
}

function onSendRecordOpenChange(value: boolean): void {
  isSendRecordOpen.value = value
  if (!value) sendRecordContext.value = null
}

async function onRecordSaved(): Promise<void> {
  const savedId = sendRecordContext.value?.doctorStationId ?? null
  onSendRecordOpenChange(false)
  await Promise.all([store.fetchList(), refreshAttackRow(savedId)])
}

/**
 * #3097 — "Registro de Plantão" no passo 1: o modal emite o payload da vaga e se fecha.
 * Sem este handler o evento se perdia e o wizard de folha nunca abria. Permissão não se
 * checa aqui: o servidor só oferece "Enviar registro" a quem tem `attendance_emitting`.
 */
function onRedirectToSheet(payload: RedirectToSheetPayload): void {
  const handoff = buildSheetHandoff(payload)
  sheetInitialForm.value = handoff.form
  sheetLockedFields.value = handoff.lockedFields
  sheetDoctorStationId.value = sendRecordContext.value?.doctorStationId ?? null
  onSendRecordOpenChange(false)
  isSheetUploadOpen.value = true
}

/** Folha enviada. Quem fecha é o próprio wizard (com "enviar outra" ele recomeça aberto). */
async function onSheetSaved(): Promise<void> {
  await Promise.all([store.fetchList(), refreshAttackRow(sheetDoctorStationId.value)])
}

/**
 * Depois do envio, a linha do ataque passa a mostrar a situação nova (e deixa
 * de oferecer "Enviar registro agora"), mesmo fora da página ou do filtro.
 * Falha na busca mantém a linha do retrato: o ataque segue.
 */
async function refreshAttackRow(doctorStationId: number | null): Promise<void> {
  if (doctorStationId === null || !attackRows.value.has(doctorStationId)) return
  try {
    const fresh = await store.fetchRowById(doctorStationId)
    if (!fresh || !attackRows.value.has(doctorStationId)) return
    attackRows.value = new Map(attackRows.value).set(doctorStationId, fresh)
  } catch {
    // Segue com a linha do retrato.
  }
}

function openContactForm(
  row: PriorityPaymentRow,
  channel: PriorityContactChannel = 'WHATSAPP'
): void {
  contactDefaultChannel.value = channel
  contactRow.value = row
}

/** Ponto único das ações de linha, do cartão do Kanban e do cartão do ataque. */
function onRowAction(payload: RowActionPayload): void {
  switch (payload.key) {
    case 'DETAILS':
      goToDetails(payload.row)
      return
    case 'SEND_RECORD':
      void openSendRecord(payload.row)
      return
    case 'AUDIT':
    case 'VIEW_AUDIT':
      goToAudit(payload.row)
      return
    case 'REGISTER_CONTACT':
      openContactForm(payload.row)
      return
    case 'COPY_MESSAGE':
      // Só o painel de ataque copia, e ele chama `onCopyAttackMessage` direto —
      // nenhuma linha oferece essa chave.
      return
  }
}

/**
 * Copiar NÃO grava o contato (decisão do dono, Q20). O que o histórico precisa
 * — canal e observação — só existe depois que a pessoa disser como falou e o
 * que aconteceu; gravar "WhatsApp, sem observação" a cada cópia encheria o
 * histórico de linhas que ninguém escreveu. A cópia apenas ABRE o formulário,
 * já com WhatsApp marcado, porque foi de lá que a pessoa veio.
 */
async function copyMessage(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    copyFeedback.value = PRIORITY_COPY_FEEDBACK
    return true
  } catch (error) {
    notifyError(error, 'Não foi possível copiar a mensagem de cobrança')
    return false
  }
}

async function onCopyAttackMessage(): Promise<void> {
  const current = attack.current.value
  if (!current || !attackMessage.value) return
  if (await copyMessage(attackMessage.value)) openContactForm(current, 'WHATSAPP')
}

async function onSubmitContact(payload: {
  doctorStationId: number
  channel: PriorityContactChannel
  note?: string
}): Promise<void> {
  if (isSavingContact.value) return
  isSavingContact.value = true
  try {
    await store.registerContact(payload.doctorStationId, {
      channel: payload.channel,
      ...(payload.note ? { note: payload.note } : {})
    })
    contactRow.value = null
    // No meio do ataque a fila NÃO é recarregada: trocar as linhas aqui mudaria
    // o plantão que está na tela entre o contato e o "Tratado, próximo".
    if (!attack.isActive.value) await store.fetchList()
  } catch (error) {
    notifyError(error, 'Não foi possível registrar o contato')
  } finally {
    isSavingContact.value = false
  }
}

function onAttackPrimary(): void {
  const current = attack.current.value
  if (!current) return
  if (current.situation === 'AWAITING_AUDIT') {
    goToAudit(current)
    return
  }
  void openSendRecord(current)
}

/**
 * O aviso de conflito é zerado quando a PESSOA avança (ruling C3). Com uma
 * transição em voo o `useAttackQueue` ignora o clique, e o aviso fica.
 */
function onAttackDone(): void {
  if (attack.isBusy.value) return
  attackConflictMessage.value = null
  attackMissingMessage.value = null
  void attack.markDone()
}

function onAttackSkip(): void {
  if (attack.isBusy.value) return
  attackConflictMessage.value = null
  attackMissingMessage.value = null
  void attack.skip()
}

/** Guarda de reentrada da barra de lote: um clique por vez (ver Task 8). */
async function runBulkAction(action: () => Promise<void>): Promise<void> {
  if (isBulkActionRunning.value) return
  isBulkActionRunning.value = true
  try {
    await action()
  } finally {
    isBulkActionRunning.value = false
  }
}

/**
 * Resolve ids em linhas: primeiro a página atual; o que faltar (fila do
 * filtro inteiro, seleção feita em outra página) vem de UMA busca do recorte
 * (`fetchScopeRows`, em páginas de 200 até 1000). Falha nessa busca não derruba a ação: os ids
 * ficam em `missingIds` e quem chama avisa que foram pulados.
 */
async function resolveRows(
  ids: number[]
): Promise<{ rows: PriorityPaymentRow[]; missingIds: number[] }> {
  const pageById = new Map(store.rows.map((row) => [row.doctorStationId, row]))
  const fetchedById = new Map<number, PriorityPaymentRow>()
  const outsidePage = ids.filter((id) => !pageById.has(id))
  if (outsidePage.length) {
    try {
      for (const row of await store.fetchScopeRows(outsidePage))
        fetchedById.set(row.doctorStationId, row)
    } catch {
      // Segue sem as linhas de fora da página; elas viram `missingIds`.
    }
  }
  const rows: PriorityPaymentRow[] = []
  const missingIds: number[] = []
  for (const id of ids) {
    const row = pageById.get(id) ?? fetchedById.get(id)
    if (row) rows.push(row)
    else missingIds.push(id)
  }
  return { rows, missingIds }
}

function onStartAttack(): Promise<void> {
  return runBulkAction(async () => {
    try {
      const ids = await store.fetchAttackQueue(store.selectedIds)
      if (!ids.length) {
        notifyErrorMessage(ATTACK_EMPTY_MESSAGE)
        return
      }
      const { rows, missingIds } = await resolveRows(ids)
      attackConflictMessage.value = null
      attackMissingCount.value = missingIds.length
      attackTakenCount.value = 0
      attackMissingMessage.value = missingIds.length ? ROWS_MISSING_MESSAGE : null
      attackRows.value = new Map(rows.map((row) => [row.doctorStationId, row]))
      if (!rows.length) {
        if (missingIds.length) notifyErrorMessage(ROWS_MISSING_MESSAGE)
        return
      }
      // Só entram na fila os ids resolvidos, na ordem do servidor: o "N de M"
      // não salta, e os ausentes aparecem no resumo como pulados.
      await attack.start(rows.map((row) => row.doctorStationId))
    } catch (error) {
      notifyError(error, 'Não foi possível montar a fila de ataque')
    }
  })
}

/** "Sair" e "Voltar para a lista" fecham o ataque do mesmo jeito. */
async function onFinishAttack(): Promise<void> {
  await attack.stop()
  attackRows.value = new Map()
  attackMissingCount.value = 0
  attackTakenCount.value = 0
  attackMissingMessage.value = null
  attackConflictMessage.value = null
  store.clearSelection()
  await store.fetchList()
}

function onBulkAction(key: string): Promise<void> {
  return runBulkAction(async () => {
    if (key !== 'copy') return
    // A seleção sobrevive à troca de página: resolve também as de fora dela.
    const { rows, missingIds } = await resolveRows(store.selectedIds)
    if (missingIds.length) notifyErrorMessage(ROWS_MISSING_MESSAGE)
    if (!rows.length) return
    // Em lote também não se grava contato: são N plantões e um único canal e
    // observação não descreveriam N conversas diferentes.
    await copyMessage(buildPriorityBulkCollectionMessage(rows))
  })
}

/** Toda mudança de recorte reescreve a URL: a fila filtrada é compartilhável. */
function syncUrl(): void {
  const query = priorityFiltersToUrl(store.filters, store.page)
  // `tab=` só existe no shell de abas (hub desligado); sem ele, o recarregar
  // cairia na aba padrão em vez desta fila.
  if (!useFeatureFlags().flags.enabledApprovalsHub) query.tab = 'priorityPayments'
  void router.replace({ query })
}

async function onFiltersPatch(patch: Partial<PriorityPaymentsFilters>): Promise<void> {
  await store.setFilters(patch)
  syncUrl()
}

/**
 * A ordenação da Tabela é do SERVIDOR (`orderBy`), não um `sort()` local. "Tempo"
 * é a ordem PRIORITY (`compareBySla`); clicar de novo numa coluna ativa que não
 * é o Tempo volta à ordem padrão.
 */
async function onSortChange(nextSort: QueueSort): Promise<void> {
  const orderBy = PRIORITY_SLA_SORT_KEY_TO_ORDER_BY[nextSort.key]
  if (!orderBy) return
  await onFiltersPatch({ orderBy: orderBy === store.filters.orderBy ? 'PRIORITY' : orderBy })
}

const onSelectTab = (tab: PriorityTab) => onFiltersPatch({ tab })

/** Clicar na linha do relógio liga o prazo; clicar de novo (ou no ×) desliga. */
const onToggleBand = (band: PrioritySlaBand) =>
  onFiltersPatch({ slaBand: store.filters.slaBand === band ? null : band })

/** O atalho SUBSTITUI o filtro de estado (spec §8.2). */
const onSelectStates = (slaStates: PrioritySlaState[]) => onFiltersPatch({ slaStates })

const onSelectOverdue = () => onFiltersPatch({ onlyOverdue: !store.filters.onlyOverdue })

async function onClearFilters(): Promise<void> {
  await store.clearFilters()
  syncUrl()
}

// A store corrige sozinha uma página além do fim (ver `fetchList`): a URL
// acompanha. Nas mudanças feitas pela tela, reescreve a mesma query.
watch(
  () => store.page,
  () => syncUrl()
)

async function onPageChange(nextPage: number): Promise<void> {
  await store.setPage(nextPage)
  syncUrl()
}

const onRefresh = () => store.fetchList()

/** O botão do erro não empilha cargas: uma em voo já é a tentativa. */
function onRetry(): void {
  if (store.isLoading || store.isRefetching) return
  void store.fetchList()
}

async function onExport(): Promise<void> {
  // A store não tem guarda de reentrada; a barra desabilita o botão, e isto
  // cobre o clique que chega antes de o `disabled` renderizar.
  if (store.isExporting) return
  try {
    await store.exportXlsx()
  } catch (error) {
    notifyError(error, 'Não foi possível exportar os pagamentos prioritários')
  }
}

/** A2 (25/09): o chip do contador baixa só a lista do Antecipe pago sem horário. */
async function onExportAntecipePaidUnconfirmed(): Promise<void> {
  if (store.isExporting) return
  try {
    await store.exportXlsx('ANTECIPE_PAID_UNCONFIRMED')
  } catch (error) {
    notifyError(error, 'Não foi possível exportar a lista do Antecipe pago sem horário')
  }
}

onMounted(() => {
  void store.fetchList()
})

// Recorte, resposta e seleção são desta visita: voltar à tela por outro link
// não pode mostrar, nem por um instante, a fila de outro recorte.
onUnmounted(() => {
  // Sair da tela no meio do ataque solta a marca do item corrente: senão ela
  // fica presa até expirar e bloqueia colegas por nada.
  void attack.stop()
  store.$reset()
})
</script>
